import { useMemo } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IconBadge } from "@/DesignSystem";
import { CareElementGridHelper } from "@/Flowsheets/Helpers/CareElementGridHelper";
import { FrequencyDateHelper } from "@/Flowsheets/Helpers/FrequencyDateHelper";
import { CareElementFrequencyType } from "@/Flowsheets/Models/CareElementFrequencyType";

import styles from "./CareElementFrequencySubLabel.module.scss";

interface CareElementFrequencySubLabelProps {
    id: string;
    frequency: CareElementFrequencyType;
    encounterDate: Date;
    dateReviewed?: Date | null;
    dataCy?: string;
}

/**
 * Component for rendering a care element frequency sub-label.
 * @param props - The properties for the component.
 * @property {string} props.id - The unique identifier for the component.
 * @property {CareElementFrequencyType} props.frequency - The frequency type of the care element.
 * @property {Date} props.encounterDate - The date of the encounter.
 * @property {Date | null} [props.dateReviewed] - The date the care element was reviewed.
 * @property {string} [props.dataCy] - Optional data attribute for testing purposes.
 * @returns The rendered component.
 */
function CareElementFrequencySubLabel({
    id,
    frequency,
    encounterDate,
    dateReviewed,
    dataCy
}: Readonly<CareElementFrequencySubLabelProps>): JSX.Element {
    const { getText } = useTranslation();
    const { value, showAlert } = useMemo(() => {
        if (!frequency) {
            return { value: null, showAlert: false };
        }

        return {
            value: CareElementGridHelper.handleFrequencyLabel(frequency, getText),
            showAlert: FrequencyDateHelper.shouldShowFrequencyWarningBadge(frequency, encounterDate, dateReviewed)
        };
    }, [frequency, encounterDate, dateReviewed, getText]);

    if (frequency === CareElementFrequencyType.None || !value)
        return null;

    return (
        <div
            className={styles.careElementFrequencySubLabel}
            {...(showAlert ? { title: getText("Flowsheets_OutsideFrequencyTooltipLabel") } : {})}
            data-cy={dataCy}
        >
            <p className={showAlert ? styles.highlight : styles.regularText}>
                {value}
            </p>
            <div id={id}>
                {showAlert && (
                    <IconBadge
                        dataCy="care-element-grid-warning-icon"
                        size="xs"
                        variant="warning"
                    />
                )}
            </div>
        </div>
    );
}

export { CareElementFrequencySubLabel };
